import constants from '@playcastdotio/GlobalConfig';
import {
  PlaycastAnalyticsEvent,
  PlaycastAnalyticsOptions,
  PlaycastAnalyticsVersion,
  PlaycastAnalyticsTier1Filter,
  PlaycastAnalyticsTier2Filter,
} from './types';
import type { AnalyticsBase } from '@playcastdotio/AnalyticsCore';
import { captureBrowserData } from './browserDataCapture';
import { getAnalyticsIdentity } from './context';
import { sendToAwsAnalytics } from './awsTransport';

type AnalyticsEvent = PlaycastAnalyticsEvent | AnalyticsBase;

const version: PlaycastAnalyticsVersion = {
  version: constants.VERSION,
  environment: constants.ENVIRONMENT,
};

const debug_mode = !(constants.BRANCH === 'release');

export function AnalyticsJestStub(): string {
  return 'success';
}

export const sendAnalytics = async (
  event: AnalyticsEvent,
  options?: PlaycastAnalyticsOptions,
): Promise<string> => {
  const identity = getAnalyticsIdentity(event.role);
  const enrichedEvent = { ...event };

  const mutableEvent = enrichedEvent as Record<string, unknown>;
  const identityRecord = identity as Record<string, string | undefined>;
  const identityFields = ['hostAuthId', 'playerAuthId', 'machineId', 'persistentId', 'hostClassification'];

  for (const field of identityFields) {
    if (field in identity && identityRecord[field] !== undefined && !(field in event)) {
      mutableEvent[field] = identityRecord[field];
    }
  }

  const tier1Filters = (mutableEvent['tier1Filters'] as PlaycastAnalyticsTier1Filter[] | undefined) || (['all-analytics'] as PlaycastAnalyticsTier1Filter[]);
  const tier2Filters = (mutableEvent['tier2Filters'] as PlaycastAnalyticsTier2Filter[] | undefined) || ([] as PlaycastAnalyticsTier2Filter[]);

  const browserData = captureBrowserData();

  if (!mutableEvent['persistentId']) {
    console.warn('[Analytics] Event missing persistentId, this may cause inconsistent user tracking:', event.name);
  }

  if (options?.delay && options.delay > 0) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          const result = await sendAnalytics(enrichedEvent as AnalyticsEvent, {
            ...options,
            delay: 0,
          });
          resolve(result);
        } catch (error) {
          console.error('[Analytics] Delayed send failed:', error);
          resolve('analytics failed: ' + event.name);
        }
      }, options.delay);
    });
  }

  try {
    await sendToAwsAnalytics(enrichedEvent as AnalyticsEvent, tier1Filters, tier2Filters, {
      version,
      debugMode: debug_mode,
      browserData,
    });

    return 'analytics sent: '.concat(event.name);
  } catch (error) {
    console.error('[Analytics] Failed to send event:', {
      eventName: event.name,
      error: error instanceof Error ? error.message : String(error),
    });

    return 'analytics failed: ' + event.name;
  }
};
